import React, { useEffect, useRef, useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import {
  Autocomplete, Box, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography,
} from '@mui/material'
import { Icon, T } from '../PartnerSignups/onboardingDesign'
import { FieldLabel, GhostButton, LinkButton, Notice, PrimaryButton } from '../PartnerSignups/onboardingUi'

/**
 * Super admin: credit an onboarded restaurant to one of RushMyFood's agents,
 * an ISO or an ISO's agent, from a day (backend utils/restaurantCredit.js).
 * They earn on its orders from then on at their current terms; a restaurant
 * someone else has moves, and theirs ends that day.
 */

const CREDIT_FIELDS = 'restaurantId restaurantName address kind vendorId name isoName since minStart'
const SEARCH = gql`query CreditRestaurants($search: String!) { creditRestaurants(search: $search) { ${CREDIT_FIELDS} } }`
const ASSIGNEES = gql`query CreditAssignees { creditAssignees { vendorId kind name isoName terms minStart } creditToday }`
const ASSIGN = gql`
  mutation AssignRestaurantCredit($restaurantId: ID!, $vendorId: ID!, $startsOn: String!) {
    assignRestaurantCredit(restaurantId: $restaurantId, vendorId: $vendorId, startsOn: $startsOn) { ${CREDIT_FIELDS} }
  }
`
const END = gql`mutation EndRestaurantCredit($restaurantId: ID!) { endRestaurantCredit(restaurantId: $restaurantId) { ${CREDIT_FIELDS} } }`

const GROUP = { AGENT: 'RushMyFood agents', ISO: 'ISOs', ISO_AGENT: 'ISO agents' }
const dayText = ymd => (ymd ? new Date(`${ymd}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '')
const who = c => (c.kind === 'ISO_AGENT' ? `${c.name} (${c.isoName})` : c.name)
const latest = (...days) => days.filter(Boolean).sort().pop() || null
const cap = t => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t)

// presetVendorId: opened from someone's restaurants, so they are already picked.
export default function AssignRestaurantDialog({ onClose, onDone, presetVendorId = null }) {
  const [text, setText] = useState('')
  const [search, setSearch] = useState('')
  const [restaurant, setRestaurant] = useState(null)
  const [assignee, setAssignee] = useState(null)
  const [startsOn, setStartsOn] = useState('')
  const [error, setError] = useState(null)
  const { data: a } = useQuery(ASSIGNEES, { fetchPolicy: 'network-only' })
  const { data: s, loading: searching } = useQuery(SEARCH, { variables: { search }, skip: search.length < 2, fetchPolicy: 'network-only' })
  const [assign, { loading: assigning }] = useMutation(ASSIGN)
  const [end, { loading: ending }] = useMutation(END)
  const today = a && a.creditToday
  const options = ((a && a.creditAssignees) || []).slice().sort((x, y) => Object.keys(GROUP).indexOf(x.kind) - Object.keys(GROUP).indexOf(y.kind))
  const results = (s && s.creditRestaurants) || []
  const minStart = latest(restaurant && restaurant.minStart, assignee && assignee.minStart)
  const busy = assigning || ending

  // Search as they type, a moment after they stop.
  useEffect(() => {
    const t = setTimeout(() => setSearch(text.trim()), 300)
    return () => clearTimeout(t)
  }, [text])
  useEffect(() => { if (today && !startsOn) setStartsOn(today) }, [today, startsOn])
  // Picked once when the list arrives; they can still change or clear it.
  const preset = useRef(presetVendorId)
  useEffect(() => {
    if (!preset.current) return
    const match = options.find(o => String(o.vendorId) === String(preset.current))
    if (match) {
      setAssignee(match)
      preset.current = null
    }
  }, [options])
  // A date before what's allowed moves up to the first allowed day.
  useEffect(() => { if (minStart && startsOn && startsOn < minStart) setStartsOn(minStart) }, [minStart, startsOn])

  const run = async (fn, message) => {
    setError(null)
    try {
      await fn()
      onDone(message)
    } catch (e) {
      setError(e.message)
    }
  }

  const moving = restaurant && restaurant.kind
  const summary = restaurant && assignee && startsOn && (
    moving
      ? `${restaurant.restaurantName} moves from ${who(restaurant)} to ${who(assignee)} on ${dayText(startsOn)}. ${who(restaurant)} stops earning on it that day.`
      : `${who(assignee)} earns on ${restaurant.restaurantName}'s orders from ${dayText(startsOn)}.`
  )

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign a restaurant</DialogTitle>
      <DialogContent dividers sx={{ borderColor: T.divider }}>
        <Typography variant="body2" sx={{ color: T.text2, mb: 2.5 }}>
          Credit a restaurant that’s already on RushMyFood to one of your agents, an ISO or an ISO’s agent. They earn on its orders from the start date at their current terms, as if it had come in through their link.
        </Typography>
        {error && <Notice severity="error" onClose={() => setError(null)}>{error}</Notice>}

        <FieldLabel htmlFor="assign-search" label="Restaurant" />
        {restaurant ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 1.5, mb: 2.5, borderRadius: '12px', border: `1px solid ${T.cardBorder}`, background: T.footerBg }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{restaurant.restaurantName}</Typography>
              <Typography sx={{ color: restaurant.kind ? T.text2 : T.hint, fontSize: 13 }}>
                {restaurant.kind ? `Credited to ${who(restaurant)} since ${new Date(restaurant.since).toLocaleDateString()}` : 'Not credited to anyone'}
              </Typography>
            </Box>
            <LinkButton size="small" onClick={() => setRestaurant(null)}>Change</LinkButton>
          </Box>
        ) : (
          <Box sx={{ mb: 2.5 }}>
            <TextField
              id="assign-search" fullWidth size="small" autoFocus placeholder="Search by name or ID"
              value={text} onChange={e => setText(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Icon.Search size={16} /></InputAdornment>,
                endAdornment: searching ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null,
              }}
            />
            {search.length >= 2 && !searching && results.length === 0 && (
              <Typography sx={{ color: T.hint, fontSize: 13.5, mt: 1 }}>No restaurant matches “{search}”.</Typography>
            )}
            {results.length > 0 && (
              <Box sx={{ mt: 1, border: `1px solid ${T.cardBorder}`, borderRadius: '12px', overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
                {results.map(r => (
                  <ButtonBase
                    key={r.restaurantId} onClick={() => setRestaurant(r)}
                    sx={{ display: 'block', width: '100%', textAlign: 'left', px: 1.5, py: 1.1, borderTop: `1px solid ${T.divider}`, '&:first-of-type': { borderTop: 0 }, '&:hover': { background: T.fill } }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{r.restaurantName}</Typography>
                    <Typography sx={{ color: T.hint, fontSize: 12.5 }}>
                      {[r.address, r.kind ? `credited to ${who(r)}` : 'not credited'].filter(Boolean).join(' · ')}
                    </Typography>
                  </ButtonBase>
                ))}
              </Box>
            )}
          </Box>
        )}

        <FieldLabel htmlFor="assign-to" label="Credit to" />
        <Autocomplete
          id="assign-to" size="small" options={options} value={assignee} onChange={(e, v) => setAssignee(v)}
          groupBy={o => GROUP[o.kind]} getOptionLabel={o => who(o)}
          isOptionEqualToValue={(o, v) => o.vendorId === v.vendorId}
          renderOption={(props, o) => (
            <Box component="li" {...props} key={o.vendorId} sx={{ display: 'block !important' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{who(o)}</Typography>
              <Typography sx={{ fontSize: 12.5, color: T.hint }}>{cap(o.terms)}</Typography>
            </Box>
          )}
          renderInput={params => <TextField {...params} placeholder="An agent, ISO or ISO agent" />}
          sx={{ mb: assignee ? 1 : 2.5 }}
        />
        {assignee && <Typography sx={{ color: T.text2, fontSize: 13, mb: 2.5 }}>{cap(assignee.terms)}</Typography>}

        <FieldLabel htmlFor="assign-start" label="Start date" />
        <TextField
          id="assign-start" type="date" size="small" value={startsOn} onChange={e => setStartsOn(e.target.value)}
          inputProps={{ min: minStart || undefined, max: today || undefined }}
          helperText={minStart
            ? `Orders from this day count. It can be in the past, from ${dayText(minStart)} on: earlier months are already settled.`
            : 'Orders from this day count. It can be in the past.'}
          sx={{ minWidth: 220 }}
        />

        {summary && <Notice severity="info" sx={{ mt: 2.5, mb: 0 }}>{summary}</Notice>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        {moving && (
          <LinkButton
            disabled={busy} sx={{ color: T.muted, mr: 'auto', '&:hover': { color: T.text, background: T.fill } }}
            onClick={() => run(() => end({ variables: { restaurantId: restaurant.restaurantId } }), `${who(restaurant)} no longer earns on ${restaurant.restaurantName}.`)}
          >
            Stop crediting {who(restaurant)}
          </LinkButton>
        )}
        <GhostButton onClick={onClose} disabled={busy}>Cancel</GhostButton>
        <PrimaryButton
          busy={assigning} disabled={busy || !restaurant || !assignee || !startsOn}
          onClick={() => run(
            () => assign({ variables: { restaurantId: restaurant.restaurantId, vendorId: assignee.vendorId, startsOn } }),
            `${restaurant.restaurantName} is credited to ${who(assignee)} from ${dayText(startsOn)}.`,
          )}
        >
          Assign
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  )
}
